import React, { useMemo } from 'react';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend,
  CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler
} from 'chart.js';
import { Doughnut, Line, Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler);

// Color palette
const COLORS = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#ec4899', '#14b8a6', '#84cc16'
];

/* ─── Doughnut: Spending by Category ─── */
export const SpendingDoughnut = ({ data }) => {
  const chartData = useMemo(() => ({
    labels: data.map(d => d.category),
    datasets: [{
      data: data.map(d => d.amount),
      backgroundColor: COLORS.slice(0, data.length).map(c => c + 'cc'),
      borderColor: COLORS.slice(0, data.length),
      borderWidth: 2,
      hoverOffset: 6,
    }],
  }), [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: getComputedStyle(document.documentElement).getPropertyValue('--text-secondary').trim() || '#64748b',
          font: { family: 'Inter', size: 12, weight: '500' },
          padding: 16,
          boxWidth: 12,
          borderRadius: 4,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(15,23,42,0.9)',
        titleColor: '#f1f5f9',
        bodyColor: '#94a3b8',
        padding: 12,
        cornerRadius: 10,
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
            const pct = ((ctx.raw / total) * 100).toFixed(1);
            return ` $${ctx.raw.toFixed(2)} (${pct}%)`;
          }
        }
      }
    },
  };

  if (!data || data.length === 0) {
    return (
      <div style={{ height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No spending data available</p>
      </div>
    );
  }

  return (
    <div style={{ height: '240px' }}>
      <Doughnut data={chartData} options={options} />
    </div>
  );
};

/* ─── Line: Balance Trend ─── */
export const BalanceTrendLine = ({ transactions }) => {
  const chartData = useMemo(() => {
    const monthlyMap = {};
    transactions.forEach(tx => {
      const d = new Date(tx.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyMap[key]) monthlyMap[key] = { income: 0, expense: 0 };
      if (tx.type === 'income') monthlyMap[key].income += tx.amount;
      else monthlyMap[key].expense += tx.amount;
    });

    const sorted = Object.entries(monthlyMap).sort(([a], [b]) => a.localeCompare(b));
    let runningBalance = 0;
    const labels = [];
    const balances = [];
    const incomes = [];
    const expenses = [];

    sorted.forEach(([month, { income, expense }]) => {
      runningBalance += income - expense;
      const [y, m] = month.split('-');
      labels.push(new Date(y, m - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }));
      balances.push(parseFloat(runningBalance.toFixed(2)));
      incomes.push(parseFloat(income.toFixed(2)));
      expenses.push(parseFloat(expense.toFixed(2)));
    });

    return { labels, balances, incomes, expenses };
  }, [transactions]);

  const data = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'Balance',
        data: chartData.balances,
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99,102,241,0.12)',
        fill: true,
        tension: 0.4,
        borderWidth: 2.5,
        pointBackgroundColor: '#6366f1',
        pointRadius: 4,
        pointHoverRadius: 7,
      },
      {
        label: 'Income',
        data: chartData.incomes,
        borderColor: '#10b981',
        backgroundColor: 'transparent',
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        borderDash: [5, 5],
        pointBackgroundColor: '#10b981',
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: 'Expenses',
        data: chartData.expenses,
        borderColor: '#ef4444',
        backgroundColor: 'transparent',
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        borderDash: [5, 5],
        pointBackgroundColor: '#ef4444',
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: getComputedStyle(document.documentElement).getPropertyValue('--text-secondary').trim() || '#64748b',
          font: { family: 'Inter', size: 12 },
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 16,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(15,23,42,0.9)',
        titleColor: '#f1f5f9',
        bodyColor: '#94a3b8',
        padding: 12,
        cornerRadius: 10,
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: $${ctx.raw.toLocaleString()}`,
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: getComputedStyle(document.documentElement).getPropertyValue('--text-muted').trim() || '#94a3b8',
          font: { family: 'Inter', size: 11 },
        },
        border: { display: false },
      },
      y: {
        grid: {
          color: 'rgba(148,163,184,0.1)',
          drawBorder: false,
        },
        ticks: {
          color: getComputedStyle(document.documentElement).getPropertyValue('--text-muted').trim() || '#94a3b8',
          font: { family: 'Inter', size: 11 },
          callback: (v) => `$${v.toLocaleString()}`,
        },
        border: { display: false },
      },
    },
  };

  return (
    <div style={{ height: '260px' }}>
      <Line data={data} options={options} />
    </div>
  );
};

/* ─── Bar: Monthly Income vs Expense ─── */
export const MonthlyBarChart = ({ transactions }) => {
  const chartData = useMemo(() => {
    const monthlyMap = {};
    transactions.forEach(tx => {
      const d = new Date(tx.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyMap[key]) monthlyMap[key] = { income: 0, expense: 0 };
      if (tx.type === 'income') monthlyMap[key].income += tx.amount;
      else monthlyMap[key].expense += tx.amount;
    });

    const sorted = Object.entries(monthlyMap).sort(([a], [b]) => a.localeCompare(b)).slice(-6);

    return {
      labels: sorted.map(([month]) => {
        const [y, m] = month.split('-');
        return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      }),
      incomes: sorted.map(([, v]) => parseFloat(v.income.toFixed(2))),
      expenses: sorted.map(([, v]) => parseFloat(v.expense.toFixed(2))),
    };
  }, [transactions]);

  const data = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'Income',
        data: chartData.incomes,
        backgroundColor: 'rgba(16,185,129,0.85)',
        borderRadius: 6,
        borderSkipped: false,
      },
      {
        label: 'Expenses',
        data: chartData.expenses,
        backgroundColor: 'rgba(239,68,68,0.85)',
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: getComputedStyle(document.documentElement).getPropertyValue('--text-secondary').trim() || '#64748b',
          font: { family: 'Inter', size: 12 },
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 16,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(15,23,42,0.9)',
        titleColor: '#f1f5f9',
        bodyColor: '#94a3b8',
        padding: 12,
        cornerRadius: 10,
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: $${ctx.raw.toLocaleString()}`,
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: getComputedStyle(document.documentElement).getPropertyValue('--text-muted').trim() || '#94a3b8',
          font: { family: 'Inter', size: 11 },
        },
        border: { display: false },
      },
      y: {
        grid: { color: 'rgba(148,163,184,0.1)', drawBorder: false },
        ticks: {
          color: getComputedStyle(document.documentElement).getPropertyValue('--text-muted').trim() || '#94a3b8',
          font: { family: 'Inter', size: 11 },
          callback: (v) => `$${v.toLocaleString()}`,
        },
        border: { display: false },
      },
    },
  };

  return (
    <div style={{ height: '260px' }}>
      <Bar data={data} options={options} />
    </div>
  );
};
